import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, ValidatorFn, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, combineLatest, map, startWith, Subject, takeUntil } from 'rxjs';
import { AllClients } from 'src/app/Interfaces/SQL-morning-responses';
import { TruckDistributionUsers } from 'src/app/Interfaces/truck-distribution';
import { Users } from 'src/app/Interfaces/users';
import { LoadingStatuses } from 'src/app/Interfaces/util';
import { StoreService } from 'src/app/services/store.service';
import { TruckDistributionService } from 'src/app/services/truck-distribution.service';
import { genericSearchingFunction } from 'src/app/Utility/functions';
import { translateDateForSQLServer } from 'src/app/Utility/date_functions/SQL_ISO_adapter';
import { environment } from 'src/environments/environment';

@Component({
	selector: 'app-kanban-assignment-new-task-popup',
	templateUrl: './kanban-assignment-new-task-popup.component.html',
	styleUrls: ['./kanban-assignment-new-task-popup.component.scss'],
})
export class KanbanAssignmentNewTaskPopupComponent implements OnInit, OnDestroy {
	_truckDistribution = inject(TruckDistributionService);
	fb = inject(FormBuilder);
	dialogRef = inject(MatDialogRef<KanbanAssignmentNewTaskPopupComponent>);
	_ss = inject(StoreService);

	taskSubmitStatus$ = new BehaviorSubject<LoadingStatuses>('idle');

	allClients$ = this._truckDistribution.clients$;
	allAgents$ = this._truckDistribution.usersForAssignment$;

	isLoadingClients$ = new BehaviorSubject<boolean>(false);

	form = this.fb.group({
		client: ['', this.isClientValid()],
		date: ['', Validators.required],
		agent: ['', this.isAgentValid()],
		remarks: [''],
	});

	allClientsDisplay$ = combineLatest([this.form.controls.client.valueChanges.pipe(startWith('')), this.allClients$]).pipe(map(([value, clients]) => this._filterClient(value || '')));

	allAgentsDisplay$ = combineLatest([this.form.controls.agent.valueChanges.pipe(startWith('')), this.allAgents$]).pipe(map(([value, agents]) => this._filterAgents(value || '')));

	today = new Date();

	destroy$ = new Subject<void>();

	shouldRefreshTasks$ = new BehaviorSubject<boolean>(false);

	constructor() {}

	ngOnInit(): void {
		if (this._truckDistribution.clients$.value.length) return;

		this.isLoadingClients$.next(true);
		this._truckDistribution.getAllClientsFromClientsTable().subscribe({
			next: res => {
				if (typeof res === 'string') {
					alert('לא ניתן לטעון את רשימת הלקוחות');
					this.closePopup();
					return;
				}
				this.allClients$.next(res.recordset);
				this.isLoadingClients$.next(false);
			},
			error: err => {
				alert('לא ניתן לטעון את רשימת הלקוחות');
				this.closePopup();
			},
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();

		this.dialogRef.close(this.shouldRefreshTasks$.value);
	}

	closePopup(): void {
		this.dialogRef.close(this.shouldRefreshTasks$.value);
	}

	handleTaskSubmit() {
		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}
		// this.dialogRef.close(this.form.value);

		this.taskSubmitStatus$.next('loading');

		const { client: clientValue, agent: agentValue, date: dateValue, remarks } = this.form.value;

		const client = this.allClients$.value.find(client => client.ClientId === clientValue);
		const agent = this.allAgents$.value.find(agent => agent.AgentID === agentValue);
		const date = translateDateForSQLServer(new Date(dateValue));

		// type 8888 hardcoded for now

		this._truckDistribution
			.handleCreateNewTask({ client, agent, date: date.toISOString(), typeofTask: 8888, remarks: remarks || '' })
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: res => {
					if (typeof res === 'string') {
						this.taskSubmitStatus$.next('error');
						return;
					}

					this.taskSubmitStatus$.next('success');
					this.shouldRefreshTasks$.next(true);

					this.form.reset();
					setTimeout(() => {
						if (this.taskSubmitStatus$.value === 'success') this.taskSubmitStatus$.next('idle');
					}, 3000);
				},
				error: err => {
					this.taskSubmitStatus$.next('error');
				},
			});
	}

	private _filterClient(name: string | AllClients): AllClients[] {
		const filterValue = typeof name === 'string' ? name.toLowerCase() : '';

		return genericSearchingFunction(filterValue, this.allClients$.value, ['Client_Name', 'ClientId']);
	}
	private _filterAgents(name: string | TruckDistributionUsers): TruckDistributionUsers[] {
		const filterValue = typeof name === 'string' ? name.toLowerCase() : '';

		return genericSearchingFunction(filterValue, this.allAgents$.value, ['AgentID', 'UserName']);
	}

	private isClientValid(): ValidatorFn {
		return control => {
			const client = this.allClients$.value.find(c => c.ClientId === control.value);
			if (!client) {
				return { required: true };
			}
			return null;
		};
	}

	private isAgentValid(): ValidatorFn {
		return control => {
			const agent = this.allAgents$.value.find(c => c.AgentID === control.value);
			if (!agent) {
				return { required: true };
			}
			return null;
		};
	}
}
